import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SSOTransition } from '../components/SSOTransition';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf623078-725f-4e36-b08f-8b0b55ed25bb/artifacts/jewke8hu_image.png";

// URL de la Centrale Prysma (variable d'environnement - RÈGLE 6)
const CENTRALE_URL = process.env.REACT_APP_CENTRALE_URL;

export const SSOCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithSSO, logout } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, transition, error
  const [errorMessage, setErrorMessage] = useState('');
  const [userName, setUserName] = useState('');
  const hasProcessed = useRef(false); // Prevent double processing

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get('token');
    
    // RÈGLE 3: Ne JAMAIS faire confiance au paramètre "user" sans valider le token
    // On ignore complètement les autres paramètres URL (user, username, email, etc.)
    // Seul le token JWT fait foi
    
    if (!token) {
      // RÈGLE 5: En cas d'échec, ne pas conserver l'ancienne session
      logout();
      setStatus('error');
      setErrorMessage('Aucun token SSO fourni dans l\'URL');
      return;
    }

    const validateSSO = async () => {
      try {
        // RÈGLE 1: TOUJOURS effacer la session existante AVANT de valider un nouveau token SSO
        logout();
        
        // RÈGLE 2: Le sso_token n'est pas stocké, il est envoyé une seule fois au backend
        // Le backend retourne un token d'application différent
        const userData = await loginWithSSO(token);
        
        // Récupérer le nom de l'utilisateur pour l'animation
        const displayName = userData.first_name 
          ? `${userData.first_name} ${userData.last_name || ''}`.trim()
          : userData.name || userData.username || 'Utilisateur';
        
        setUserName(displayName);
        setStatus('transition'); // Afficher l'animation de transition
        
      } catch (error) {
        // RÈGLE 5: En cas d'échec de validation, ne PAS conserver l'ancienne session
        logout();
        
        setStatus('error');
        const detail = error.response?.data?.detail;
        if (typeof detail === 'object') {
          setErrorMessage(detail.message || 'Erreur de connexion SSO');
        } else {
          setErrorMessage(detail || 'Token SSO invalide ou expiré');
        }
        console.error('SSO Error:', error);
      }
    };

    validateSSO();
  }, [searchParams, loginWithSSO, logout]);

  // Redirection vers la Centrale
  const handleRedirectToCentrale = () => {
    if (!CENTRALE_URL) {
      toast.error('URL de la Centrale non configurée');
      return;
    }
    window.location.href = CENTRALE_URL;
  };

  // Callback appelé quand l'animation de transition est terminée
  const handleTransitionComplete = () => {
    toast.success('Connexion SSO réussie');
    navigate('/');
  };

  // Afficher l'animation de transition SSO
  if (status === 'transition') {
    return (
      <SSOTransition 
        onComplete={handleTransitionComplete}
        userName={userName}
      />
    );
  }

  // Afficher l'écran d'erreur
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Prysm'Analytics" 
              className="w-20 h-20 mb-4"
            />
            <h1 className="font-heading text-3xl font-bold text-white">
              Prysm'Analytics
            </h1>
          </div>

          <Card className="bg-card border-border">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl text-white font-heading">
                Erreur de connexion
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Impossible de vous connecter automatiquement
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-400 text-sm text-center">{errorMessage}</p>
              {CENTRALE_URL && (
                <Button 
                  onClick={handleRedirectToCentrale}
                  className="mt-4 bg-primary text-black hover:bg-primary/90"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Retourner à la Centrale Prysma
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // État de chargement initial (avant validation)
  return (
    <SSOTransition 
      onComplete={() => {}}
      userName=""
    />
  );
};

export default SSOCallback;
